import { useMemo } from 'react';
import { useStatisticsContext } from '../../../../../providers/StatisticsProvider';
import { useGetCustomersQuery } from '../../../../../slices/customersApiSlice';
import { useGetOrderPrimaryPhaseStatsQuery } from '../../../../../slices/statisticsApiSlice';
import { useGetAssistantUsersQuery, useGetSaleUsersQuery } from '../../../../../slices/usersApiSlice';
import { buildStatisticsQueryParams } from '../../../filters/lib/statisticsFilterParams';
import {
	isPipelineBuiltinChartKind,
	resolvePersistedChartIncludeOrders,
	resolvePersistedChartKind,
	PERSISTED_CHART_KIND,
} from '../lib/statisticsPersistedCharts';
import { isFrozenChartSnapshot } from '../lib/statisticsChartRefreshMode';
import { resolvePersistedChartViewFilters } from '../lib/dashboardChartLifecycle';
import { resolveFrozenChartBundle } from '../lib/chartDataSnapshot';
import { resolveDashboardChartBundleEntry } from '../lib/buildDashboardChartBundle';

const useDashboardChartBundle = (snapshot, { isManager = false, skip = false, forceLive = false } = {}) => {
	const { overdueData, isLoading: isOverdueLoading, statisticsFilters } = useStatisticsContext();
	const chartKind = snapshot ? resolvePersistedChartKind(snapshot) : null;
	const isOverdueChart = chartKind === PERSISTED_CHART_KIND.OVERDUE;
	const isPipelineBuiltinChart = isPipelineBuiltinChartKind(chartKind);
	const isPageBoundPipeline = isPipelineBuiltinChart && Boolean(snapshot?.usesPageFilters);
	const skipDataFetch = skip || isOverdueChart || isPageBoundPipeline;
	const frozenBundle = useMemo(() => {
		if (!snapshot || forceLive || !isFrozenChartSnapshot(snapshot)) return null;
		const bundle = resolveFrozenChartBundle(snapshot);
		if (!bundle) return null;
		const yearMonthsBroken = Boolean(bundle.trendYearMonthsMode) && !bundle.trendPeriodYoy?.years?.length;
		return yearMonthsBroken ? null : bundle;
	}, [forceLive, snapshot]);
	const useFrozenBundle = Boolean(frozenBundle);

	const { data: fetchedSaleUsers = [] } = useGetSaleUsersQuery({ scope: 'order' }, { skip: skipDataFetch || useFrozenBundle });
	const { data: fetchedAssistantUsers = [] } = useGetAssistantUsersQuery({ scope: 'order' }, { skip: skipDataFetch || useFrozenBundle });
	const { data: fetchedCustomers = [] } = useGetCustomersQuery(undefined, { skip: skipDataFetch || !isManager || useFrozenBundle });

	const filters = useMemo(
		() => (snapshot && !isOverdueChart && !isPageBoundPipeline ? resolvePersistedChartViewFilters(snapshot, statisticsFilters) : null),
		[isOverdueChart, isPageBoundPipeline, snapshot, statisticsFilters],
	);
	const queryParams = useMemo(
		() =>
			filters
				? buildStatisticsQueryParams(filters, {
						includeOrders: resolvePersistedChartIncludeOrders(snapshot),
						compositionCompareSeries: Boolean(snapshot?.chartConfig?.compareSeries),
					})
				: undefined,
		[filters, snapshot],
	);

	const { data: primaryPhaseStats, isLoading, isFetching } = useGetOrderPrimaryPhaseStatsQuery(queryParams, {
		skip: skipDataFetch || !queryParams || useFrozenBundle,
		refetchOnMountOrArgChange: true,
	});

	const chartBundle = useMemo(() => {
		if (!snapshot) return null;
		if (useFrozenBundle) return frozenBundle;
		if (isPageBoundPipeline) return null;
		if (isOverdueChart) {
			return resolveDashboardChartBundleEntry({
				snapshot,
				overdueData,
				isOverdueLoading,
			});
		}
		if (!filters && !isPipelineBuiltinChart) return null;
		return resolveDashboardChartBundleEntry({
			snapshot,
			primaryPhaseStats,
			overdueData,
			isOverdueLoading,
			fetchedSaleUsers,
			fetchedAssistantUsers,
			fetchedCustomers,
			isStatisticsQueryLoading: isLoading,
			isStatisticsQueryFetching: isFetching,
			forceLive: true,
		});
	}, [
		fetchedAssistantUsers,
		fetchedCustomers,
		fetchedSaleUsers,
		filters,
		frozenBundle,
		isPageBoundPipeline,
		isPipelineBuiltinChart,
		isFetching,
		isLoading,
		isOverdueChart,
		isOverdueLoading,
		overdueData,
		primaryPhaseStats,
		snapshot,
		useFrozenBundle,
	]);

	return { chartBundle, filters, isLoading, isFetching };
};

export default useDashboardChartBundle;
